import React, { useEffect, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getFuelEUData } from "../../services/complianceService";
import BenchmarkYearPicker from "../../components/Common/BenchmarkYearPicker";
import ExportToExcel from "../../components/Common/ExportToExcel";

const FuelEU = () => {
  const location = useLocation();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVesselId, setSelectedVesselId] = useState(null);
  const [mainSpreadsheetData, setMainSpreadsheetData] = useState([[]]);
  const [segmentSpreadsheetData, setSegmentSpreadsheetData] = useState([[]]);

  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { currentVesselId } = location.state || {};

  const benchmarkYear = useSelector((state) => state.benchmarkYear.year);

  useEffect(() => {
    if (currentVesselId) {
      setSelectedVesselId(currentVesselId);
    } else if (currentVessel?._id) {
      setSelectedVesselId(currentVessel._id);
    }
  }, [currentVesselId, currentVessel?._id]);

  useEffect(() => {
    if (!selectedVesselId) return;

    const fetchFuelEUData = async () => {
      setLoading(true);
      try {
        const res = await getFuelEUData(selectedVesselId, benchmarkYear);
        if (res.success) {
          setApiData(res.data);
          const transformedData = transformFuelEUData(res.data);
          const segmentData = transformSegmentData(res.data);
          setMainSpreadsheetData(transformedData);
          setSegmentSpreadsheetData(segmentData);
        } else {
          console.error("Failed to load Fuel EU data:", res.error);
          setApiData(null);
        }
      } catch (err) {
        console.error("Error fetching Fuel EU data:", err);
        setApiData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFuelEUData();
  }, [selectedVesselId, benchmarkYear]);

  // Helper function to safely format values
  const formatValue = (value, defaultValue = "N/A", fixed = null) => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    if (fixed !== null && typeof value === "number") {
      return value.toFixed(fixed);
    }
    return value.toString();
  };

  const transformFuelEUData = (data) => {
    if (!data) return [[]];

    const headers = [];
    const values = [];

    const addRow = (label, value, className = "") => {
      headers.push({
        value: label,
        readOnly: true,
        className: `header-cell ${className}`,
      });
      values.push({
        value: value,
        readOnly: true,
        className: className,
      });
    };

    // Vessel Information Section
    addRow("Vessel Name", data.vesselInfo?.name || "N/A");
    addRow("IMO Number", data.vesselInfo?.imoNumber || "N/A");
    addRow("Vessel Type", data.vesselInfo?.type || "N/A");
    addRow(
      "Is Container Vessel",
      data.vesselInfo?.type === "Container ships" ? "Yes" : "No"
    );
    addRow("Ice Class", data.vesselInfo?.iceClass || "N/A");
    addRow("Ice Class Type", data.vesselInfo?.iceClassType || "N/A");
    addRow("Year", formatValue(data.year));
    addRow(
      "GHG Intensity Target",
      data.regulationParticulars?.ghgIntensityTarget || "N/A"
    );

    // Regulation Particulars Section
    addRow(
      "Compliance Balance",
      data.regulationParticulars?.fuelEUComplianceBalance || "N/A"
    );
    addRow(
      "Fuel EU Remedial Penalty / Compliance Surplus",
      data.regulationParticulars
        ?.fuelEUAssociatedRemedialPenaltyOrComplianceSurplus || "N/A"
    );

    // Get totals from segment breakdown
    const totals = data.segmentBreakdown?.find(
      (segment) => segment.segment === "TOTALS"
    );

    if (totals) {
      addRow("Overall Status", totals.status || "N/A");
    }

    return [headers, values];
  };

  // Transform segment breakdown data to spreadsheet format
  const transformSegmentData = (data) => {
    if (!data?.segmentBreakdown) return [[]];

    const segments = data.segmentBreakdown;
    const isContainerVessel = data.vesselInfo?.type === "Container ships";

    // Filter segments based on vessel type
    const filteredSegments = isContainerVessel
      ? segments // Include all segments for container vessels
      : segments.filter(
          (segment) => !segment.segment.includes("Transhipment") // Exclude transhipment segments for non-container vessels
        );

    // Create headers
    const headers = [
      { value: "Segment", readOnly: true, className: "section-header" },
      {
        value: "CO2 Equivalent Well to Tank HFO",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "CO2 Equivalent Well to Tank LFO",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "CO2 Equivalent Well to Tank MGO",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Well to Tank Intensity",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Tank to Wake Intensity",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Well to Wake Intensity",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Well to Tank Emissions",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Tank to Wake Emissions",
        readOnly: true,
        className: "header-cell",
      },
      {
        value: "Well to Wake Emissions",
        readOnly: true,
        className: "header-cell",
      },
      { value: "Compliance Balance", readOnly: true, className: "header-cell" },
      { value: "Penalty Amount", readOnly: true, className: "header-cell" },
      { value: "Status", readOnly: true, className: "header-cell" },
      { value: "Total Energy Used", readOnly: true, className: "header-cell" },
      { value: "Ice Class Applied", readOnly: true, className: "header-cell" },
    ];

    // Create rows for each segment
    const rows = filteredSegments.map((segment) => {
      const isTranshipmentSegment = segment.segment.includes("Transhipment");
      const cellClass = isTranshipmentSegment
        ? "transhipment-cell"
        : "segment-cell";

      return [
        { value: segment.segment, readOnly: true, className: cellClass },
        {
          value: `${formatValue(
            segment.co2EquivalentWellToTankHFO,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.co2EquivalentWellToTankLFO,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.co2EquivalentWellToTankMGO,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.wellToTankIntensity,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.tankToWakeIntensity,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.wellToWakeIntensity,
            "0",
            2
          )} gCO₂eq/MJ`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.wellToTankEmissions,
            "0",
            2
          )} · 10⁶ g CO₂-eq`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.tankToWakeEmissions,
            "0",
            2
          )} · 10⁶ g CO₂-eq`,
          readOnly: true,
        },
        {
          value: `${formatValue(
            segment.wellToWakeEmissions,
            "0",
            2
          )} · 10⁶ g CO₂-eq`,
          readOnly: true,
        },
        {
          value: formatValue(segment.complianceBalance, "0", 2),
          readOnly: true,
        },
        {
          value: `${formatValue(segment.penaltyAmount, "0", 2)} EUR`,
          readOnly: true,
        },
        { value: segment.status, readOnly: true },
        { value: formatValue(segment.totalEnergyUsed, "0", 2), readOnly: true },
        { value: segment.iceClassApplied, readOnly: true },
      ];
    });

    // Add section header for transhipment if container vessel AND there are transhipment segments
    if (isContainerVessel) {
      const regularSegments = rows.filter(
        (row) => !row[0].value.includes("Transhipment")
      );
      const transhipmentSegments = rows.filter((row) =>
        row[0].value.includes("Transhipment")
      );

      // Only add transhipment header if there are actual transhipment segments
      if (transhipmentSegments.length > 0) {
        // Create section header row for transhipment
        const transhipmentHeader = [
          {
            value: "TRANSHIPMENT PORT SEGMENTS (Container Vessels Only)",
            readOnly: true,
            className: "transhipment-header",
            colSpan: 15,
          },
          ...Array(14).fill({
            value: "",
            readOnly: true,
            className: "transhipment-header",
          }),
        ];

        return [
          headers,
          ...regularSegments,
          transhipmentHeader,
          ...transhipmentSegments,
        ];
      }
    }

    return [headers, ...rows];
  };

  const handleMainSpreadsheetChange = (data) => {
    setMainSpreadsheetData(data);
  };

  const handleSegmentSpreadsheetChange = (data) => {
    setSegmentSpreadsheetData(data);
  };

  if (loading) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="Fuel EU Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading Fuel EU data...</span>
        </div>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="Fuel EU Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-500">No Fuel EU compliance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans text-sm min-h-screen bg-gray-50">
      <div className="flex items-center justify-between p-4 border-b border-gray-300">
        <VesselHeader pageTitle="Fuel EU Compliance Data" />
        <div className="flex space-x-2">
          <BenchmarkYearPicker />
          <ExportToExcel />
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Summary Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Fuel EU Compliance Summary
            </h2>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[800px] p-4">
              <Spreadsheet
                data={mainSpreadsheetData}
                onChange={handleMainSpreadsheetChange}
                hideRowIndicators={true}
                hideColumnIndicators={true}
                className="enhanced-spreadsheet"
              />
            </div>
          </div>
        </div>

        {/* Segment Breakdown Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Segment Breakdown
            </h2>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[2000px] p-4">
              <Spreadsheet
                data={segmentSpreadsheetData}
                onChange={handleSegmentSpreadsheetChange}
                hideRowIndicators={true}
                hideColumnIndicators={true}
                className="enhanced-spreadsheet"
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .enhanced-spreadsheet {
          --selected-cell: #e3f2fd;
          --selected-row: #f5f5f5;
          --selected-column: #f5f5f5;
        }
        .enhanced-spreadsheet table {
          border-collapse: separate;
          border-spacing: 0;
          width: auto !important;
        }
        .enhanced-spreadsheet td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 8px 12px !important;
          transition: background-color 0.2s;
          white-space: nowrap;
          color: #1f2937 !important;
          background-color: white !important;
          font-size: 0.875rem !important;
        }
        .enhanced-spreadsheet td:first-child {
          border-left: 1px solid #e5e7eb !important;
        }
        .header-cell {
          background-color: #f1f5f9 !important;
          font-weight: 700 !important;
          color: #374151 !important;
          min-width: 150px !important;
        }

        .section-header {
          background-color: #1e40af !important;
          color: white !important;
          font-weight: 700 !important;
          font-size: 1em !important;
          text-align: center !important;
        }

        .segment-cell {
          background-color: #f8fafc !important;
          font-weight: 600 !important;
          color: #1e293b !important;
          min-width: 200px !important;
        }

        .enhanced-spreadsheet tr:hover td {
          background-color: #f9fafb !important;
        }

        .enhanced-spreadsheet .active-cell {
          box-shadow: inset 0 0 0 2px #3b82f6 !important;
        }

        .transhipment-cell {
          background-color: #fff7ed !important;
          font-weight: 600 !important;
          color: #ea580c !important;
          border-left: 3px solid #ea580c !important;
        }

        .transhipment-header {
          background-color: #7c2d12 !important;
          color: white !important;
          font-weight: 700 !important;
          font-size: 1.1em !important;
          text-align: center !important;
        }
      `}</style>
    </div>
  );
};

export default FuelEU;
